/**
 * screens/products/crm.tsx — CRM product surface (UI shell, mock data).
 * Faithful RN port of design/products.jsx CRMPage + ContactEditor: stat strip,
 * Contacts table + Pipeline kanban (stage-move arrows), add/edit drawer.
 * Wires to the CRM API later (platform-only scope for now).
 */

import React, { useState } from 'react';
import { View, Text, ScrollView, Pressable } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { usePlatform } from '../../context/platformContext';
import { useToast } from '../../components/toast';
import { ProductHeader, StatStrip, PButton, PTextInput, PSelect, PEmpty } from '../../components/platform/controls';
import { DataTable, Tag, SideDrawer } from '../../components/platform/primitives';
import Tabs from '../../components/tabs';
import Avatar from '../../components/avatar';
import Icon from '../../components/icon';
import { FS, FW, R, Tone, inr } from '../../utils/platformTokens';

type Contact = typeof import('../../data/platformSeed').SEED.crmContacts[number];
const STAGES = ['Lead', 'Qualified', 'Proposal', 'Won'];
const STAGE_TONE: Record<string, Tone> = { Lead: 'neutral', Qualified: 'info', Proposal: 'warn', Won: 'success' };
const OWNERS = ['Anjali Verma', 'Raj Iyer', 'Sara Mathew'];

const ContactEditor: React.FC<{ open: boolean; onClose: () => void; contact: Contact | null }> = ({ open, onClose, contact }) => {
  const { setState } = usePlatform();
  const toast = useToast();
  const isNew = !contact;
  const blank: any = { name: '', company: '', email: '', phone: '', stage: 'Lead', owner: 'Anjali Verma', value: 0 };
  const [form, setForm] = useState<any>(blank);
  const [err, setErr] = useState<{ name?: string; email?: string }>({});
  React.useEffect(() => { if (open) { setForm(contact ? { ...contact } : blank); setErr({}); } }, [open, contact]);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const save = () => {
    const e: typeof err = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = 'Enter a valid email.';
    setErr(e); if (Object.keys(e).length) return;
    setState((s) => ({
      ...s,
      crmContacts: isNew ? [{ ...form, id: 'C-' + Math.floor(1000 + Math.random() * 9000) }, ...s.crmContacts] : s.crmContacts.map((x) => (x.id === contact!.id ? { ...form } : x)),
    }));
    toast.success(isNew ? 'Contact added.' : 'Contact updated.'); onClose();
  };
  return (
    <SideDrawer open={open} onClose={onClose} title={isNew ? 'Add contact' : `Edit ${contact?.name}`}
      footer={<><PButton kind="ghost" onPress={onClose}>Cancel</PButton><PButton kind="accent" onPress={save}>{isNew ? 'Add contact' : 'Save changes'}</PButton></>}>
      <View style={{ gap: 14 }}>
        <PTextInput label="Full name" value={form.name} onChangeText={(v) => set('name', v)} error={err.name} />
        <PTextInput label="Company" value={form.company} onChangeText={(v) => set('company', v)} />
        <PTextInput label="Email" keyboardType="email-address" value={form.email} onChangeText={(v) => set('email', v)} error={err.email} />
        <PTextInput label="Phone" mono value={form.phone} onChangeText={(v) => set('phone', v)} />
        <View style={{ flexDirection: 'row', gap: 12 }}>
          <View style={{ flex: 1 }}><PSelect label="Stage" value={form.stage} onChange={(v) => set('stage', v)} options={STAGES.map((s) => ({ value: s, label: s }))} /></View>
          <View style={{ flex: 1 }}><PTextInput label="Deal value (₹)" mono keyboardType="numeric" value={String(form.value)} onChangeText={(v) => set('value', +v || 0)} /></View>
        </View>
        <PSelect label="Owner" value={form.owner} onChange={(v) => set('owner', v)} options={OWNERS.map((o) => ({ value: o, label: o }))} />
      </View>
    </SideDrawer>
  );
};

const CrmScreen: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const { state, setState } = usePlatform();
  const toast = useToast();
  const [tab, setTab] = useState('contacts');
  const [q, setQ] = useState('');
  const [editor, setEditor] = useState<{ open: boolean; contact: Contact | null }>({ open: false, contact: null });

  const contacts = state.crmContacts;
  const filtered = contacts.filter((x) => `${x.name} ${x.company} ${x.email} ${x.id}`.toLowerCase().includes(q.toLowerCase()));
  const open = contacts.filter((x) => x.stage !== 'Won');
  const pipelineValue = open.reduce((s, x) => s + x.value, 0);
  const won = contacts.filter((x) => x.stage === 'Won');

  const moveStage = (ct: Contact, dir: number) => {
    const i = STAGES.indexOf(ct.stage);
    const ni = Math.max(0, Math.min(STAGES.length - 1, i + dir));
    if (ni === i) return;
    setState((s) => ({ ...s, crmContacts: s.crmContacts.map((x) => (x.id === ct.id ? { ...x, stage: STAGES[ni] } : x)) }));
  };

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1200, width: '100%', alignSelf: 'center' }}>
      <ProductHeader title="CRM" sub="Contacts, deals and your sales pipeline."
        action={<PButton kind="accent" icon="plus" onPress={() => setEditor({ open: true, contact: null })}>Add contact</PButton>} />
      <StatStrip items={[
        { label: 'Total contacts', value: contacts.length },
        { label: 'Open deals', value: open.length },
        { label: 'Pipeline value', value: inr(pipelineValue) },
        { label: 'Won this quarter', value: won.length, tone: c.success },
      ]} />

      <Tabs value={tab} onChange={setTab} tabs={[{ id: 'contacts', label: 'Contacts', count: contacts.length }, { id: 'pipeline', label: 'Pipeline' }]} />
      <View style={{ height: 16 }} />

      {tab === 'contacts' ? (
        <>
          <View style={{ maxWidth: 320, marginBottom: 14 }}>
            <PTextInput leadingIcon="search" placeholder="Search contacts…" value={q} onChangeText={setQ} />
          </View>
          <View style={{ borderWidth: 1, borderColor: c.line, borderRadius: R.r3, backgroundColor: c.card, overflow: 'hidden' }}>
            <DataTable<Contact>
              minWidth={760}
              keyExtractor={(x) => x.id}
              rows={filtered}
              empty={<PEmpty icon="search" title="No contacts found">Try a different search.</PEmpty>}
              columns={[
                {
                  key: 'contact', header: 'Contact', width: 260,
                  render: (x) => (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <Avatar name={x.name} size="sm" />
                      <View>
                        <Text style={{ fontWeight: FW.medium, color: c.ink }}>{x.name}</Text>
                        <Text style={{ fontSize: FS.sm, color: c.ink3 }}>{x.company} · {x.id}</Text>
                      </View>
                    </View>
                  ),
                },
                { key: 'stage', header: 'Stage', width: 120, render: (x) => <Tag tone={STAGE_TONE[x.stage]}>{x.stage}</Tag> },
                { key: 'owner', header: 'Owner', width: 140, render: (x) => <Text style={{ color: c.ink2, fontSize: FS.md }}>{x.owner}</Text> },
                { key: 'value', header: 'Deal value', width: 120, render: (x) => <Text style={{ fontFamily: theme.font.mono, color: c.ink }}>{inr(x.value)}</Text> },
                {
                  key: 'actions', header: 'Actions', align: 'right',
                  render: (x) => (
                    <View style={{ flexDirection: 'row', gap: 6, justifyContent: 'flex-end' }}>
                      <PButton size="sm" kind="ghost" onPress={() => toast.info(`Opening ${x.name}'s record…`)}>View</PButton>
                      <PButton size="sm" kind="ghost" icon="edit" iconOnly onPress={() => setEditor({ open: true, contact: x })} />
                    </View>
                  ),
                },
              ]}
            />
          </View>
        </>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={{ flexDirection: 'row', gap: 12 }}>
            {STAGES.map((stage) => {
              const col = contacts.filter((x) => x.stage === stage);
              const total = col.reduce((s, x) => s + x.value, 0);
              return (
                <View key={stage} style={{ width: 240, backgroundColor: c.paper2, borderRadius: R.r3, padding: 10 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 4, paddingBottom: 6 }}>
                    <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: STAGE_TONE[stage] === 'neutral' ? c.ink4 : (c as any)[STAGE_TONE[stage]] }} />
                    <Text style={{ fontWeight: FW.semibold, fontSize: FS.sm, color: c.ink }}>{stage}</Text>
                    <Text style={{ color: c.ink4, fontSize: FS.sm }}>{col.length}</Text>
                  </View>
                  <Text style={{ fontSize: FS.xs, color: c.ink3, paddingHorizontal: 4, paddingBottom: 8, fontFamily: theme.font.mono }}>{inr(total)}</Text>
                  <View style={{ gap: 8 }}>
                    {col.map((x) => (
                      <View key={x.id} style={{ backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: R.r3, padding: 12 }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <Avatar name={x.name} size="sm" />
                          <View style={{ flex: 1 }}>
                            <Text numberOfLines={1} style={{ fontWeight: FW.medium, fontSize: FS.sm, color: c.ink }}>{x.name}</Text>
                            <Text style={{ fontSize: FS.xs, color: c.ink3 }}>{x.company}</Text>
                          </View>
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Text style={{ fontFamily: theme.font.mono, fontSize: FS.sm, color: c.ink }}>{inr(x.value)}</Text>
                          <View style={{ flexDirection: 'row', gap: 2 }}>
                            <Pressable onPress={() => moveStage(x, -1)} disabled={x.stage === 'Lead'} style={{ width: 24, height: 24, borderRadius: R.r1, borderWidth: 1, borderColor: c.line, alignItems: 'center', justifyContent: 'center', backgroundColor: c.card, opacity: x.stage === 'Lead' ? 0.4 : 1 }}>
                              <Icon name="chev-l" size={14} color={c.ink2} />
                            </Pressable>
                            <Pressable onPress={() => moveStage(x, 1)} disabled={x.stage === 'Won'} style={{ width: 24, height: 24, borderRadius: R.r1, borderWidth: 1, borderColor: c.line, alignItems: 'center', justifyContent: 'center', backgroundColor: c.card, opacity: x.stage === 'Won' ? 0.4 : 1 }}>
                              <Icon name="chev-r" size={14} color={c.ink2} />
                            </Pressable>
                          </View>
                        </View>
                      </View>
                    ))}
                  </View>
                </View>
              );
            })}
          </View>
        </ScrollView>
      )}

      <ContactEditor open={editor.open} contact={editor.contact} onClose={() => setEditor({ open: false, contact: null })} />
    </ScrollView>
  );
};

export default CrmScreen;
